/**
 * What a 404 from a DELETE actually means.
 *
 * DELETE is idempotent: the caller is asking for a resource to be gone, and a
 * 404 says it already is. That is the requested end state, not a failure —
 * but `makeAuthenticatedRequest` turns every non-2xx into a rejection, so the
 * delete paths in `Bookmarks.vue` fell into their catch block, showed
 * "couldn't delete this bookmark", and left the card sitting in the list. The
 * link was gone on the server the whole time; it reappeared as deleted only
 * after the next reload, which reads as an app that loses track of itself.
 *
 * Sentry linkflare LINKFLARE-14 / 142807252. Server-side tracing
 * (`service.name=api`, DELETE on the links route) shows what is behind it:
 * two deletes of the SAME link about 1.3 s apart, the first answered 200 and
 * the second 404. Nothing was wrong with either request — the client simply
 * sent the delete twice, which a confirm button on a phone will do, and the
 * second one is the one the user was shown an error for.
 *
 * The 404 is still worth counting: a rising rate means the UI is double-firing
 * deletes, which IS actionable. So the request keeps reporting it, flagged via
 * `expectedErrorStatuses` so it files at warning under its own fingerprint
 * instead of paging as an unexpected error (the LINKFLARE-Y mechanism, not
 * LINKFLARE-Z's silent drop — this is authenticated in-app traffic whose
 * volume is bounded by real usage).
 *
 * Kept dependency-free and side-effect-free at import time so the policy is
 * unit-testable without a network or a Vue app — same shape as
 * `recipeSaveOutcome.js` and `requestRetry.js`.
 */

import { isForeignError } from './apiErrorFacts.js'

/**
 * Did this rejected request fail only because the target was already gone?
 *
 * Keyed off the STATUS, never the response body: the body is English-only,
 * owned by the backend, and differs between "Link not found" and whatever a
 * proxy substitutes. Matched against the message because that is where
 * `makeAuthenticatedRequest` puts the status — the same way
 * `recipeSaveOutcome.isNoRecipeFound` and `reportApiFailure` read it.
 *
 * Deliberately narrow: only a 404 counts. A 403 is NOT "already gone" — it is
 * someone else's link, and silently reporting success would tell the user we
 * deleted something we did not.
 *
 * @param {Error} error — whatever the DELETE rejected with
 * @returns {boolean}
 */
export function isAlreadyDeleted(error) {
  // A 404 an intercepting proxy invented is not our backend reporting a missing
  // row — the request never reached the API, so the link's fate is unknown and
  // resolving would claim a deletion that may not have happened.
  if (isForeignError(error)) return false
  return /^HTTP error! status: 404/.test(error?.message || '')
}

/**
 * Did this resolved delete actually remove a row?
 *
 * `deleteLink` resolves either way — that is the point of treating DELETE as
 * idempotent — so the only thing separating "we deleted it" from "it was
 * already gone" is the marker it returns. Callers keeping their own counts
 * (`userStats.bookmarkCount`, seeded once at startup and mutated locally after)
 * must ask THIS, not whether the card was still on screen: the loaded list
 * holds only the current pages, so a genuine delete for a link scrolled or
 * filtered out of view would go uncounted and the count would drift the other
 * way. It gates the free-plan limit, so drift in either direction is wrong.
 *
 * Exported so the view, the batch helper and their tests share one definition
 * rather than each re-deriving it (lessons.md: a test that re-implements the
 * branch it is testing can be wrong in the same way and stay green).
 *
 * @param {*} deleteResult — whatever `ApiService.deleteLink()` resolved with
 * @returns {boolean}
 */
export function shouldCountDeletion(deleteResult) {
  return !deleteResult?.alreadyDeleted
}
